import React, { useRef } from "react";
import { convertText } from "./helpers";
import PropTypes from "prop-types";
import SFDragDrop from "../dragDrop";
import getStyled from "../../hooks/getStyled";
import {
  SFGridItem,
  SFGridContainerItem,
  SFStack,
  SFGridContainer,
} from "../grid";
import { SFTextSnippetIcon, SFDragNDropFileIcon, SFCloseIcon } from "../icons";
import SFIconButton from "../button/iconButton";
import SFTypography from "../typography";
import { Trans } from "react-i18next";

const { styled } = getStyled();

const SFTypographyLabelStyled = styled("label")(({ theme }) => ({
  ...theme.typography.h3,
  color: theme.palette.blue500.main,
  cursor: "pointer",
}));

const StyleFileDetailsContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `solid 1px ${theme.palette.grey250.main}`,
  borderRadius: "5px",
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
}));

const SFUpload = ({
  onFileSelected,
  onFileRemoved,
  delimeter = ",",
  uploadFromText = false,
  fileTypeName,
  fileName,
  fileSize,
  acceptedFileTypes,
  messagei18nKey = null,
}) => {
  const fileInputRef = useRef();

  const fileUploadHandler = async (file) => {
    if (file) {
      let res = await onFileSelected(file);
      if (!res) {
        removeFileHandler();
      }
    }
  };

  const removeFileHandler = async () => {
    fileInputRef.current.value = null;
    onFileRemoved();
  };

  if (uploadFromText) {
    return (
      <React.Fragment>
        <textarea
          onChange={async (e) => {
            const result = await convertText({
              text: e.target.value,
              hasHeaders: false,
              delimeter,
            });
            await onFileSelected(result);
          }}
        />
      </React.Fragment>
    );
  }

  return (
    <SFStack spacing={3}>
      <SFDragDrop
        handleDrop={async (e) => {
          await fileUploadHandler(e.dataTransfer.files[0]);
        }}
      >
        <SFGridContainer spacing={1}>
          <SFGridItem xs={12}>
            <SFGridContainerItem justifyContent="center">
              <SFGridItem>
                <label htmlFor="fileInput">
                  <SFDragNDropFileIcon size="xxxlarge" />
                </label>
              </SFGridItem>
            </SFGridContainerItem>
          </SFGridItem>
          <SFGridItem xs={12}>
            <SFGridContainerItem justifyContent="center">
              <SFGridItem>
                <SFTypography variant="h3" component="span">
                  {messagei18nKey ? (
                    <Trans
                      i18nKey={messagei18nKey}
                      components={{
                        style: <SFTypographyLabelStyled htmlFor="fileInput" />,
                      }}
                    />
                  ) : (
                    <Trans
                      i18nKey="components.fileUploader.dragAndDropText"
                      components={{
                        style: <SFTypographyLabelStyled htmlFor="fileInput" />,
                      }}
                    />
                  )}
                </SFTypography>
                <input
                  accept={acceptedFileTypes}
                  ref={fileInputRef}
                  hidden
                  type="file"
                  id={"fileInput"}
                  onChange={async (e) => {
                    await fileUploadHandler(e.target.files[0]);
                  }}
                />
              </SFGridItem>
            </SFGridContainerItem>
          </SFGridItem>
        </SFGridContainer>
      </SFDragDrop>

      {fileName && (
        <SFStack>
          <StyleFileDetailsContainer>
            <SFGridContainer justifyContent="space-between" alignItems="center">
              <SFGridItem xs="auto">
                <SFStack direction="row" alignItems="center" spacing={1}>
                  <SFTextSnippetIcon color="blue500" />
                  <SFTypography variant="body1bold" component="span">
                    {fileName}
                  </SFTypography>
                </SFStack>
              </SFGridItem>
              <SFGridItem xs="auto">
                <SFStack direction="row" alignItems="center">
                  <SFTypography variant="body1bold" component="span">
                    {fileSize}
                  </SFTypography>
                  <SFIconButton onClick={removeFileHandler}>
                    <SFCloseIcon color="grey500" />
                  </SFIconButton>
                </SFStack>
              </SFGridItem>
            </SFGridContainer>
          </StyleFileDetailsContainer>
        </SFStack>
      )}
    </SFStack>
  );
};

SFUpload.propTypes = {
  acceptedFileTypes: PropTypes.string,
  fileName: PropTypes.string,
  fileSize: PropTypes.string,
  onFileSelected: PropTypes.func.isRequired,
  onFileRemoved: PropTypes.func.isRequired,
  delimeter: PropTypes.string,
  uploadFromText: PropTypes.bool,
  fileTypeName: PropTypes.string,
};

export default SFUpload;
